import { Permission, PermissionMatrix, UserExtraRole } from '../types/permissions';

// Define all available permissions
export const PERMISSIONS: Record<string, Permission> = {
  // System Administration
  'system.admin': {
    id: 'system.admin',
    name: 'System Administration',
    description: 'Full system access and configuration',
    resource: 'system',
    action: 'admin'
  },
  'system.stats': {
    id: 'system.stats',
    name: 'View System Statistics',
    description: 'Access system analytics and reports',
    resource: 'system',
    action: 'read'
  },
  'system.factory_reset': {
    id: 'system.factory_reset',
    name: 'Factory Reset',
    description: 'Reset system to default state',
    resource: 'system',
    action: 'reset'
  },

  // User Management
  'users.create': {
    id: 'users.create',
    name: 'Create Users',
    description: 'Create new user accounts',
    resource: 'users',
    action: 'create'
  },
  'users.read': {
    id: 'users.read',
    name: 'View Users',
    description: 'View user accounts and profiles',
    resource: 'users',
    action: 'read'
  },
  'users.update': {
    id: 'users.update',
    name: 'Update Users',
    description: 'Modify user accounts and roles',
    resource: 'users',
    action: 'update'
  },
  'users.delete': {
    id: 'users.delete',
    name: 'Delete Users',
    description: 'Delete user accounts',
    resource: 'users',
    action: 'delete'
  },

  // Content Management
  'content.create': {
    id: 'content.create',
    name: 'Create Content',
    description: 'Create and publish content',
    resource: 'content',
    action: 'create'
  },
  'content.read': {
    id: 'content.read',
    name: 'View Content',
    description: 'Access educational content and materials',
    resource: 'content',
    action: 'read'
  },
  'content.update': {
    id: 'content.update',
    name: 'Update Content',
    description: 'Edit and modify existing content',
    resource: 'content',
    action: 'update'
  },
  'content.delete': {
    id: 'content.delete',
    name: 'Delete Content',
    description: 'Remove content from system',
    resource: 'content',
    action: 'delete'
  },

  // Academic Management
  'academic.grades': {
    id: 'academic.grades',
    name: 'Manage Grades',
    description: 'Input and modify student grades',
    resource: 'academic',
    action: 'grade'
  },
  'academic.attendance': {
    id: 'academic.attendance',
    name: 'Manage Attendance',
    description: 'Track and modify attendance records',
    resource: 'academic',
    action: 'attendance'
  },
  'academic.schedule': {
    id: 'academic.schedule',
    name: 'View Schedule',
    description: 'Access class schedules and timetables',
    resource: 'academic',
    action: 'schedule'
  },
  'academic.classes': {
    id: 'academic.classes',
    name: 'Manage Classes',
    description: 'Create and manage class assignments',
    resource: 'academic',
    action: 'manage'
  },

  // PPDB (New Student Registration)
  'ppdb.manage': {
    id: 'ppdb.manage',
    name: 'Manage PPDB',
    description: 'Manage new student registration process',
    resource: 'ppdb',
    action: 'manage'
  },
  'ppdb.approve': {
    id: 'ppdb.approve',
    name: 'Approve PPDB Applications',
    description: 'Review and approve new student applications',
    resource: 'ppdb',
    action: 'approve'
  },

  // Academic Leadership Roles
  'academic.oversight': {
    id: 'academic.oversight',
    name: 'Academic Oversight',
    description: 'Oversee academic operations and performance',
    resource: 'academic',
    action: 'oversight'
  },
  'academic.curriculum': {
    id: 'academic.curriculum',
    name: 'Curriculum Management',
    description: 'Manage and approve curriculum changes',
    resource: 'academic',
    action: 'curriculum'
  },
  'academic.teacher_evaluation': {
    id: 'academic.teacher_evaluation',
    name: 'Teacher Evaluation',
    description: 'Evaluate and assess teacher performance',
    resource: 'academic',
    action: 'evaluate'
  },
  'academic.discipline': {
    id: 'academic.discipline',
    name: 'Student Discipline',
    description: 'Manage student discipline and conduct',
    resource: 'academic',
    action: 'discipline'
  },
  'school.policies': {
    id: 'school.policies',
    name: 'School Policies',
    description: 'Create and manage school policies',
    resource: 'school',
    action: 'policies'
  },
  'school.reports': {
    id: 'school.reports',
    name: 'School Reports',
    description: 'Generate and view comprehensive school reports',
    resource: 'school',
    action: 'reports'
  },

  // Payment Management
  'payments.create': {
    id: 'payments.create',
    name: 'Create Payments',
    description: 'Create and initiate payment transactions',
    resource: 'payments',
    action: 'create'
  },
  'payments.read': {
    id: 'payments.read',
    name: 'View Payments',
    description: 'View payment history and status',
    resource: 'payments',
    action: 'read'
  },
  'payments.update': {
    id: 'payments.update',
    name: 'Update Payments',
    description: 'Update payment records and statuses',
    resource: 'payments',
    action: 'update'
  },
  'payments.delete': {
    id: 'payments.delete',
    name: 'Delete Payments',
    description: 'Delete payment records',
    resource: 'payments',
    action: 'delete'
  },

  // Special Roles
  'inventory.manage': {
    id: 'inventory.manage',
    name: 'Manage Inventory',
    description: 'Track and manage school inventory',
    resource: 'inventory',
    action: 'manage'
  },
  'osis.events': {
    id: 'osis.events',
    name: 'Manage OSIS Events',
    description: 'Organize and manage student council events',
    resource: 'osis',
    action: 'events'
  },

  // Parent Features
  'parent.monitor': {
    id: 'parent.monitor',
    name: 'Monitor Children',
    description: 'View children\'s academic progress',
    resource: 'parent',
    action: 'monitor'
  },
  'parent.reports': {
    id: 'parent.reports',
    name: 'View Reports',
    description: 'Access consolidated reports',
    resource: 'parent',
    action: 'reports'
  },
  'parent.communication': {
    id: 'parent.communication',
    name: 'Teacher Communication',
    description: 'Communicate with teachers',
    resource: 'parent',
    action: 'communicate'
  },

  // Student Features
  'student.library': {
    id: 'student.library',
    name: 'Access Library',
    description: 'Access e-library resources',
    resource: 'student',
    action: 'library'
  },
  'student.materials': {
    id: 'student.materials',
    name: 'Access Materials',
    description: 'Access learning materials and resources',
    resource: 'student',
    action: 'materials'
  },
  'quizzes.take': {
    id: 'quizzes.take',
    name: 'Take Quizzes',
    description: 'Take quizzes and submit answers',
    resource: 'quizzes',
    action: 'take'
  },
  'quizzes.view_results': {
    id: 'quizzes.view_results',
    name: 'View Quiz Results',
    description: 'View quiz results and detailed feedback',
    resource: 'quizzes',
    action: 'view_results'
  },
  'quizzes.view_history': {
    id: 'quizzes.view_history',
    name: 'View Quiz History',
    description: 'View past quiz attempts and history',
    resource: 'quizzes',
    action: 'view_history'
  }
};

// Define role-permission matrix
export const ROLE_PERMISSION_MATRIX: PermissionMatrix = {
  admin: [
    'system.admin', 'system.stats', 'system.factory_reset',
    'users.create', 'users.read', 'users.update', 'users.delete',
    'content.create', 'content.read', 'content.update', 'content.delete',
    'academic.grades', 'academic.attendance', 'academic.schedule', 'academic.classes',
    'academic.oversight', 'academic.curriculum', 'academic.teacher_evaluation', 'academic.discipline',
    'school.policies', 'school.reports',
    'ppdb.manage', 'ppdb.approve',
    'inventory.manage', 'osis.events',
    'parent.monitor', 'parent.reports', 'parent.communication',
    'student.library', 'student.materials',
    'payments.create', 'payments.read', 'payments.update', 'payments.delete'
  ],
  teacher: [
    'content.create', 'content.read', 'content.update',
    'academic.grades', 'academic.attendance', 'academic.schedule', 'academic.classes',
    'student.library', 'student.materials',
    'quizzes.view_results', 'quizzes.view_history'
  ],
  student: [
    'content.read',
    'academic.schedule',
    'student.library', 'student.materials',
    'quizzes.take', 'quizzes.view_results', 'quizzes.view_history'
  ],
  parent: [
    'parent.monitor', 'parent.reports', 'parent.communication', 'payments.read',
    'quizzes.view_results', 'quizzes.view_history'
  ]
};

// Extra role permissions (added to base role permissions)
export const EXTRA_ROLE_PERMISSIONS: Partial<Record<NonNullable<UserExtraRole>, string[]>> & { null?: string[] } = {
  wakasek: [
    'academic.oversight',
    'academic.teacher_evaluation',
    'academic.discipline',
    'academic.grades',
    'academic.attendance',
    'academic.schedule',
    'content.read',
    'users.read',
    'school.reports'
  ],
  kepsek: [
    'academic.oversight',
    'academic.curriculum',
    'academic.teacher_evaluation',
    'academic.discipline',
    'school.policies',
    'school.reports',
    'academic.grades',
    'academic.attendance',
    'academic.schedule',
    'users.read',
    'users.update',
    'content.read',
    'ppdb.approve'
  ],
  staff: ['inventory.manage'],
  osis: ['osis.events'],
  null: []
};